import type { Capability } from "./capabilities";

/**
 * Pure permission checks. Nothing here knows about React, roles or the router, so it is tested
 * in plain Node and reused by hooks, components and route guards alike.
 *
 * Hiding a control is a convenience, never security: the backend checks every request again.
 */

/** Whether `granted` (the profile's `capabilities`) includes `capability`. */
export function hasCapability(
  granted: readonly string[] | undefined,
  capability: Capability
): boolean {
  return granted?.includes(capability) ?? false;
}

/** Whether `granted` includes at least one of `capabilities`. */
export function hasAnyCapability(
  granted: readonly string[] | undefined,
  capabilities: readonly Capability[]
): boolean {
  return capabilities.some((capability) => hasCapability(granted, capability));
}

/**
 * The two shapes the API uses for `available_actions`: an object of booleans (the cohort list and
 * each cohort: `{ create: true }`) or a list of the allowed action names (the team page:
 * `["view_team", "manage_administrator_invitations"]`). Same name, two shapes (on the list for the
 * backend developer); both are read here so a screen does not care.
 */
export type ActionSet =
  Readonly<Record<string, boolean | undefined>> | readonly string[];

/**
 * Whether a resource's `available_actions` (page level, e.g. the cohort list, or item level, e.g.
 * one cohort) allows `action`. A missing set or a missing action means "no": the server only
 * advertises what the account can do.
 */
export function hasAction(
  actions: ActionSet | undefined,
  action: string
): boolean {
  if (!actions) return false;
  if (Array.isArray(actions))
    return (actions as readonly string[]).includes(action);
  return (
    (actions as Readonly<Record<string, boolean | undefined>>)[action] === true
  );
}
